import React, { createContext, useCallback, useContext, useState } from 'react'

import api from '../services/api'
import { StorageEnum } from '@/enums/storage'
import { Aluno } from '../domain/entities/aluno.entity'
import { Institution } from '../domain/entities/institution.entity'

interface AuthState {
  token: string
  student?: Aluno
  institution?: Institution
  admin?: any
}

interface SignInCredentials {
  login: string
  password: string
  role: 'student' | 'institutions' | 'admin'
}

interface AuthContextData {
  token: string
  student?: Aluno
  institution?: Institution
  admin?: any
  signIn({
    login,
    password,
    role,
  }: SignInCredentials): Promise<void | Error | any>
  signOut(): void
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

interface IAuthProvider {
  children: React.ReactNode
}

export function AuthProvider({ children }: IAuthProvider) {
  const [data, setData] = useState<AuthState>((): AuthState => {
    const token = localStorage.getItem(StorageEnum.token)
    const student = localStorage.getItem(StorageEnum.student_token)
    const institution = localStorage.getItem(StorageEnum.institution_token)
    const admin = localStorage.getItem(StorageEnum.admin)

    if (token) {
      if (student != 'undefined') {
        return {
          token,
          student: JSON.parse(student),
        }
      } else if (institution != 'undefined') {
        return {
          token,
          institution: JSON.parse(institution),
        }
      } else if (admin != 'undefined') {
        return {
          token,
          admin: JSON.parse(admin),
        }
      }
    }

    return {} as AuthState
  })

  const signIn = useCallback(
    async ({ login, password, role }: SignInCredentials) => {
      try {
        const response = await api.post(`/${role}/session`, {
          login,
          password,
        })

        const { token, student, institution, admin } = response.data

        localStorage.setItem(StorageEnum.token, token)
        localStorage.setItem(StorageEnum.student_token, JSON.stringify(student))
        localStorage.setItem(
          StorageEnum.institution_token,
          JSON.stringify(institution),
        )
        localStorage.setItem(StorageEnum.admin, JSON.stringify(admin))

        setData({ token, student, institution, admin })
      } catch (error) {
        console.error('error: ', error)
        return error
      }
    },
    [],
  )

  const signOut = useCallback(() => {
    localStorage.removeItem(StorageEnum.token)
    localStorage.removeItem(StorageEnum.student_token)
    localStorage.removeItem(StorageEnum.institution_token)
    localStorage.removeItem(StorageEnum.admin)

    setData({} as AuthState)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        token: data.token,
        student: data.student,
        institution: data.institution,
        admin: data.admin,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const authContext = useContext(AuthContext)

  if (!authContext)
    throw new Error('useAuth must be used within an AuthProvider')

  return authContext
}
